/**
 * Сопоставление field_id пресета/фильтра со справочником полей типа.
 *
 * Источник полей — `GET .../object-types/full` (после inventory 1.3 — из
 * `catalog_objects_type_field`, уже с `legacy_field_id`). Пресеты, сохранённые
 * до перенумерации, хранят legacy id (`godpovch`); актуальный id — числовой
 * (`2360`). Без резолва селект «Поле» показывает сырой код, а список условий пуст.
 */

export type FieldIdRef = {
  id: string | number
  legacy_field_id?: string | null
}

/** Находит поле по числовому или legacy id. */
export const findFieldByIdOrLegacy = <T extends FieldIdRef>(
  fields: T[],
  fieldId: string | null | undefined,
): T | null => {
  if (fieldId == null || fieldId === '') return null
  const key = String(fieldId)
  return fields.find((f) => String(f.id) === key || f.legacy_field_id === key) ?? null
}

/**
 * Возвращает актуальный (предпочтительно числовой) id поля для v-model.
 * Если сопоставить не удалось — возвращает исходный fieldId как строку.
 */
export const resolveFieldId = (
  fields: FieldIdRef[],
  fieldId: string | null | undefined,
): string | null => {
  if (fieldId == null || fieldId === '') return null
  const hit = findFieldByIdOrLegacy(fields, fieldId)
  return hit ? String(hit.id) : String(fieldId)
}

type CatalogFieldRow = {
  id?: string | number
  legacy_field_id?: string | null
}

/**
 * Проставляет `legacy_field_id` на поля, у которых его ещё нет.
 * Используется fallback-обогащением из `GET /object-types/:typeId/fields`,
 * когда full отдал поля без legacy (старый JSONB-каталог типа).
 * Идемпотентно: непустой legacy не перезаписывает.
 */
export const mergeLegacyFieldIds = (
  fields: FieldIdRef[],
  catalogRows: CatalogFieldRow[],
): void => {
  if (!fields.length || !catalogRows.length) return
  const byId = new Map<string, string>()
  for (const row of catalogRows) {
    const id = row.id == null ? '' : String(row.id)
    const legacy = row.legacy_field_id == null ? '' : String(row.legacy_field_id)
    if (id && legacy) byId.set(id, legacy)
  }
  for (const field of fields) {
    if (field.legacy_field_id) continue
    const legacy = byId.get(String(field.id))
    if (legacy) field.legacy_field_id = legacy
  }
}

/** Достаёт массив полей из ответа inventory (обёртка `{ data }` или голый массив). */
export const unwrapCatalogFieldsResponse = (res: unknown): CatalogFieldRow[] => {
  if (Array.isArray(res)) return res as CatalogFieldRow[]
  if (res && typeof res === 'object' && Array.isArray((res as { data?: unknown }).data)) {
    return (res as { data: CatalogFieldRow[] }).data
  }
  return []
}

/**
 * Нормализует поля типа для пикера расширенного поиска: id → string
 * (VSelect / пресеты сравнивают строки), остальное без изменений.
 */
export const normalizeSearchFieldIds = <T extends FieldIdRef>(fields: T[]): T[] =>
  fields.map((field) => {
    const id = field.id == null ? field.id : String(field.id)
    if (id === field.id) return field
    return { ...field, id }
  })

